import { QueryResult } from '@apollo/client';
import { ExtendedUser } from '../types';
import { useEffect, useCallback, useRef } from 'react';
import { useAuthToken } from './useAuthToken';
import { useMeLazyQuery, MeQuery, Exact } from '@src/api/graphql-codegen';
import { isHomeEDApp } from '@constants';
import { initUserSettingsFromUserData } from './useUserSettings';
import { logger } from '../utils/logger';
import { IDP_V2_REFRESH_TOKEN_NAME, persistIdpV2Tokens, refreshIdpV2Token } from '../utils/idpV2Auth';

const AUTO_LOGOUT_ENABLED = 'autoLogoutEnabled';
const LONG_REFRESH_TOKEN_THRESHOLD_MS = 24 * 60 * 60 * 1000; // 24h

type UseUserResponse = [ExtendedUser | undefined, QueryResult<MeQuery, Exact<{
  [key: string]: never;
}>>];
export const useUser = (forceUpdate?: boolean): UseUserResponse => {
  const { token, setAuthToken } = useAuthToken();
  const [getUser, query] = useMeLazyQuery({
    fetchPolicy: forceUpdate ? 'network-only' : 'cache-first',
    nextFetchPolicy: 'cache-first'
  });

  // Use ref to store the getUser function to avoid recreating fetchUser constantly
  const getUserRef = useRef(getUser);
  getUserRef.current = getUser;

  const fetchUser = useCallback(async () => {
    try {
      if (token) {
        await getUserRef.current();
      }
    } catch (e) {
      logger.info('fetching user error - ', e)
    }
  }, [token]); // Remove getUser from dependencies to prevent infinite re-renders

  useEffect(() => {
    if (token && !isHomeEDApp && !query?.loading) {
      fetchUser();
    }
  }, [token, fetchUser]); // Keep fetchUser dependency since it's now stable
  
  // Initialize user settings when user data is loaded, and re-issue the refresh token
  // if the server's autoLogoutEnabled differs from the localStorage default.
  // This handles fresh/incognito sessions where localStorage defaults to true (auto-logout on)
  // but the user's account has auto-logout disabled — without this, the backend would have
  // issued a short-TTL refresh token at login, and the user would be booted mid-session.
  useEffect(() => {
    if (!query?.data?.me) return;

    const me = query.data.me;
    initUserSettingsFromUserData(me);

    const serverAutoLogout = me.autoLogoutEnabled;
    if (serverAutoLogout === false) {
      const storedValue = localStorage.getItem(AUTO_LOGOUT_ENABLED);
      // If localStorage was at the default (null = true) before the sync just ran,
      // the backend may have issued a short-TTL refresh token. Re-issue it now.
      if (storedValue === null) {
        const refreshToken = localStorage.getItem(IDP_V2_REFRESH_TOKEN_NAME);
        if (!refreshToken) return;
        refreshIdpV2Token(refreshToken)
          .then((result) => {
            const refreshExpiresAt = Number(result.refresh_expire_date || 0);
            const refreshTtlMs = refreshExpiresAt ? refreshExpiresAt - Date.now() : 0;
            // Persist whatever came back, however short. The call above already rotated the
            // session, so the token still sitting in localStorage has been retired
            // server-side and only survives the rotation grace window. Keeping it because
            // the replacement was shorter than we hoped strands the session at the next
            // refresh - a short token beats a revoked one.
            persistIdpV2Tokens(result);
            if (result.access_token) {
              setAuthToken(result.access_token);
            }

            const isLongToken = refreshTtlMs >= LONG_REFRESH_TOKEN_THRESHOLD_MS;
            if (!isLongToken) {
              // Nothing more we can do client-side. Usually means IdPv2's own user record
              // disagrees with the autoLogoutEnabled we read from GraphQL - they live in
              // different stores, so they can drift.
              logger.warn('[useUser] Auto-logout disabled but backend issued short refresh token. TTL:', refreshTtlMs);
              return;
            }
            logger.info('[useUser] Re-issued long-TTL refresh token for auto-logout-disabled user');
          })
          .catch((err) => {
            logger.warn('[useUser] Failed to re-issue refresh token on user load:', err);
          });
      }
    }
  }, [query?.data?.me?.id, setAuthToken]); // Use user ID to prevent constant re-runs

  return [query?.data?.me || undefined, query];
};
